import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm, FormContext } from 'react-hook-form'
import { Alert } from '@material-ui/lab'
import { Box, FormControlLabel, Grid, Switch } from '@material-ui/core'

import { Label, LoadingFeedback } from 'components'
import { EmailSection } from './components'

import useDocumentGenerate from 'hooks/useDocumentGenerate'
import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import constants from 'constants/index'
import useFetch from 'hooks/useFetch'
import * as service from 'service'
import schema from './schema'

const ApprovalStep = () => {
  const [loading, setLoading] = useState(false)

  const {
    AWAITING_APPROVAL_STATUS_ID,
    ACTIVE_STATUS_ID,
    AWANTING_APPROVAL_PERSON_STATUS_ID,
  } = constants.documents

  const { response, isLoading } = useFetch(
    service.dponet.usersCompanies.list,
    {
      listByCompany: true,
      perPage: 9999999,
    },
    [],
  )
  const users = response?.data?.userCompanies || []
  const allUsers = users?.map((item) => item.user) || []

  const snackbar = useSnackbar()
  const {
    documentGenerated,
    handleNext,
    resetAdvanceStep,
    setDisabledNextButton,
    setDocumentGenerated,
    wantsToAdvanceStep,
  } = useDocumentGenerate()

  const approvalEmailType = documentGenerated?.approvalEmails

  const hasPopulatedUsers = useRef(false)

  const methods = useForm({
    validationSchema: schema,
    mode: 'onChange',
    defaultValues: {
      content: documentGenerated?.content || '',
      status: documentGenerated?.status || '',
      requireApproval: documentGenerated?.collectApproval,
      approvalType: approvalEmailType?.approvalType || 'user',
      approvalUser: [],
      approvalEmailInput: '',
      approvalEmailList:
        approvalEmailType?.approvalType === 'email'
          ? approvalEmailType?.emails || []
          : [],
    },
  })

  const { control, watch, handleSubmit } = methods

  const requireApproval = watch('requireApproval')

  useEffect(() => {
    if (
      !hasPopulatedUsers.current &&
      allUsers.length > 0 &&
      approvalEmailType?.approvalType === 'user' &&
      approvalEmailType?.emails?.length > 0
    ) {
      const matched = allUsers.filter((user) =>
        approvalEmailType.emails.includes(user.email),
      )
      methods.setValue('approvalUser', matched)
      hasPopulatedUsers.current = true
    }
  }, [allUsers])

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const status = data.requireApproval
        ? AWAITING_APPROVAL_STATUS_ID
        : ACTIVE_STATUS_ID

      const response = await service.dponet.privacyPolicies.put({
        privacyPolicyId: documentGenerated?.id,
        status: status,
        collectApproval: data.requireApproval,
      })

      const emails =
        data.requireApproval && data.approvalType === 'email'
          ? data.approvalEmailList
          : data.requireApproval
          ? data.approvalUser.map((user) => user.email)
          : []

      if (data.requireApproval) {
        await service.dponet.privacyPolicies.createPrivacyApprovals({
          privacyPolicyId: documentGenerated?.id,
          status: AWANTING_APPROVAL_PERSON_STATUS_ID,
          documentVersionId: 1,
          email: emails,
          approval_type: data.approvalType,
        })
      }

      const privacyPolicy = response.data?.privacyPolicy ?? documentGenerated

      setDocumentGenerated({
        ...privacyPolicy,
        approvalEmails: data.requireApproval
          ? { approvalType: data.approvalType, emails }
          : null,
      })
      handleNext()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (wantsToAdvanceStep) {
      handleSubmit(onSubmit)()
      resetAdvanceStep()
    }
  }, [wantsToAdvanceStep])

  useEffect(() => {
    if (!requireApproval) {
      setDisabledNextButton(false)
    }
  }, [requireApproval])

  return (
    <FormContext {...methods}>
      <Grid container spacing={2}>
        <LoadingFeedback open={loading} />
        <Grid item xs={12}>
          <Label
            title="Etapa de aprovação do documento antes da publicação"
            paddingBoxTitle={0}
          >
            <Box mt={1} mb={1}>
              <Alert variant="standard" severity="info">
                Você pode definir se o documento precisa ser aprovado. caso a
                aprovação não for definida, ele será considerado automaticamente
                aprovado pelo criador.
              </Alert>
            </Box>
            <Controller
              as={
                <FormControlLabel
                  label="Exigir aprovação antes da ativação"
                  labelPlacement="end"
                  control={<Switch color="primary" />}
                />
              }
              name="requireApproval"
              control={control}
            />
          </Label>
          {requireApproval && (
            <EmailSection
              users={allUsers}
              isLoading={isLoading}
              requireApproval={requireApproval}
              setDisabledNextButton={setDisabledNextButton}
            />
          )}
        </Grid>
      </Grid>
    </FormContext>
  )
}

export default ApprovalStep
